import React, { forwardRef, useCallback, useRef } from "react";

import styled from "veneer/styled";

import type { ItemLike } from "./DraggableTargetCard";
import DraggableTargetCard from "./DraggableTargetCard";

interface DraggableContentListProps {
  items: ReadonlyArray<ItemLike>;
  onChangeContentDuration: (duration: number, item: ItemLike) => void;
  onDeleteContent: (item: ItemLike) => void;
  onReorderContent: (items: ReadonlyArray<ItemLike>) => void;
}

const ContentDropArea = styled("div")(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: "2px dashed #f4f4f4",
  height: 175,
  overflowY: "scroll",
  padding: theme.spacing(1),
}));

export function reorder(
  items: ReadonlyArray<ItemLike>,
  startIndex: number,
  endIndex: number,
) {
  const reordered = Array.from(items);
  const [removed] = reordered.splice(startIndex, 1);
  reordered.splice(endIndex, 0, removed);
  return reordered;
}

const DraggableContentList = forwardRef<
  HTMLDivElement,
  DraggableContentListProps
>(function DraggableContentList(
  {
    items,
    onChangeContentDuration,
    onDeleteContent,
    onReorderContent,
  }: DraggableContentListProps,
  ref,
) {
  const draggingIndex = useRef<number | null>(null);

  function moveCard(dragIndex: number, hoverIndex: number) {
    const reorderedItems = reorder(items, dragIndex, hoverIndex);
    onReorderContent(reorderedItems);
  }

  const handleDragStart = useCallback((dragIndex: number) => {
    draggingIndex.current = dragIndex;
  }, []);

  const handleDragEnter = useCallback(
    (hoverIndex: number) => {
      const dragIndex = draggingIndex.current;

      if (dragIndex == null || dragIndex === hoverIndex) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      draggingIndex.current = hoverIndex;
    },
    [items, onReorderContent],
  );

  const handleDragEnd = useCallback(() => {
    draggingIndex.current = null;
  }, []);

  return (
    <ContentDropArea ref={ref}>
      {items.map((item, index) => (
        <DraggableTargetCard
          index={index}
          item={item}
          key={`${item.id}--${index}`}
          onChangeDuration={onChangeContentDuration}
          onDelete={onDeleteContent}
          onDragEnd={handleDragEnd}
          onDragEnter={handleDragEnter}
          onDragStart={handleDragStart}
        />
      ))}
    </ContentDropArea>
  );
});

export { DraggableContentList };
export type { ItemLike };
export default DraggableContentList;
